// OrderChangeForm.tsx
import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Stack,
} from "@mui/material";
import { api, Balance, BasePrice, DiscountPrepayment, DiscountQuantity, LprOrder } from "./api";
import { useFetch } from "../../../hooks/useFetch";
import InfoModal from './components/InfoModal';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from "../../../store/useAuthStore3";
import { formatDateTime } from "../../../tools/util";

/**
 * Расчет новой даты окончания обслуживания при изменении количества потребителей.
 * * @param oldEndDate - Текущая дата окончания (общая для N)
 * @param oldCount - Текущее количество потребителей (N)
 * @param newCount - Новое количество потребителей (M)
 * @returns Новая дата окончания (общая для M)
 */
export function recalculateEndDate(
    oldEndDate: Date, 
    oldCount: number, 
    newCount: number
): Date {
    if (oldCount <= 0 || newCount <= 0) return oldEndDate;
    
    const now = new Date().getTime();
    const end = oldEndDate.getTime();
    const remainingTime = end - now;

    if (remainingTime <= 0) return oldEndDate;

    const cost = remainingTime * oldCount;
    const newRemainingTime = cost / newCount;
    return new Date(now + newRemainingTime);
}

interface OrderChangeFormProps {
    balance: Balance;
    onClose: () => void;
    onSubmit: (cameras: number, newEndDate: Date) => void;
}

const OrderChangeForm: React.FC<OrderChangeFormProps> = ({ onClose, onSubmit, balance }) => {
    const { t } = useTranslation();
    const { customerId } = useAuthStore();
    const [cameras, setCameras] = useState<number>(1);
    const [newEndDate, setNewEndDate] = useState<Date | undefined>(balance.endDate);
    const { data: cameraTotalCount, } = useFetch<number>(api.getCameraTotalCount, 0);
    const { data: cameraActiveCount, } = useFetch<number>(api.getCameraActiveCount, 0);
    const { data: lprOrders, } = useFetch<LprOrder[]>(api.getLprOrder, []);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        setCameras(lprOrders[0]?.order_count || 0);
    }, [lprOrders]);

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMessage("");
        
        if (cameras < cameraActiveCount ) {
            setErrorMessage('Кількість активних камер перевищує кількість замовлених. Деактивууйте відповідну кількість камер.');
            return;
        }

        if (!newEndDate || !customerId) {
            setErrorMessage('no new EndDate');
            return;
        }

        const res = await api.changeOrder(customerId, cameras, newEndDate);
        if (res) {
            onSubmit(cameras, newEndDate);
            return;
        }
        setErrorMessage('Помилка збереження');
    };

    const calcEndDate = (newCameras: number): Date => {

        if (!balance.endDate) return new Date();

        const newEndDate = recalculateEndDate(balance.endDate, /*cameras*/ lprOrders[0]?.order_count || 0, newCameras);
        return newEndDate;
    }

    const onChangeCameras = (newCameras: number) => {
        setNewEndDate(calcEndDate(newCameras));
        setCameras(newCameras);
    };

    return (
        <Paper elevation={3} sx={{ maxWidth: 420, mx: "auto", p: 3, mt: 4 }}>
            <form onSubmit={handleSubmit}>
                <Typography variant="h5" gutterBottom>
                    {t('OrderChangeForm.Title')}
                </Typography>
                <Box mb={2} mt={4}>
                    <TextField
                        label={t('OrderForm.CamerasTitle')}
                        type="number"
                        size="small"
                        inputProps={{ min: 0 }}
                        value={cameras}
                        onChange={e => onChangeCameras(Number(e.target.value))}
                        sx={{ width: 150 }}
                    />
                </Box>

                <Box my={2}>
                    <Typography variant="h6">
                        Камер всього: {cameraTotalCount}
                    </Typography>
                    <Typography variant="h6">
                        Камер активних: {cameraActiveCount}
                    </Typography>
                    <Typography variant="h6">
                        {t('OrderChangeForm.newEndDate')}
                    </Typography>
                    <Typography variant="h6">
                        {formatDateTime(newEndDate)}
                    </Typography>
                </Box>
                
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ fontWeight: "bold", px: 4 }}
                    >
                        {t('OrderChangeForm.Submit')}
                    </Button>
                    <Button
                        type="button"
                        onClick={onClose}
                        variant="outlined"
                        color="secondary"
                    >
                        {t('OrderForm.ExitButton')}
                    </Button>
                </Stack>

            </form>

            {errorMessage && (
                <Typography mt={2} color='error'>{errorMessage}</Typography>
            )}      
            
            <InfoModal
                open={isModalOpen}
                onCancel={handleCancel}
                onAccept={() => null}
                kind="offer"
            />
        </Paper>
    );
};

export default OrderChangeForm;
